<script lang="ts">
import {defineComponent, ref, onMounted, watch} from 'vue';
import {useAuthStore} from '~/plugins/store/auth';
import {useNuxtApp} from '#app';
import {recaptcha} from '~/composables/recaptcha';
import {useReCaptcha} from 'vue-recaptcha-v3';
import {translation, useTranslationStore} from '~/plugins/store/translations';
import type {TranslationList} from '~/types/Utils';
import CustomerEmail from '~/components/customer/CustomerEmail.vue';
import CustomerPassword from '~/components/customer/CustomerPassword.vue';
import CustomerTermsAgreement from '~/components/customer/CustomerTermsAgreement.vue';
import LoadingDialog from '~/components/utils/LoadingDialog.vue';
import {
  type AuthenticateForm, createDefaultAuthenticateForm,
  createDefaultValidationAuthenticateStatus,
  type ValidationAuthenticateStatus
} from "~/types/Authenticate";
import CustomerResetToken from "~/components/customer/CustomerResetToken.vue";

export default defineComponent({
  components: {CustomerResetToken, LoadingDialog, CustomerEmail, CustomerPassword, CustomerTermsAgreement},
  props: {
    fillEmail: {
      type: String as () => string | null,
      required: false,
    },
    defaultVersion: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  setup(props, {emit}) {
    const authStore = useAuthStore();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const {$toast} = useNuxtApp();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const validationAuthenticateStatus = ref<ValidationAuthenticateStatus>(createDefaultValidationAuthenticateStatus());
    const authenticateForm = ref<AuthenticateForm>(createDefaultAuthenticateForm());
    const isEmailExists = ref(false);
    const isEmailChecked = ref(false);
    const isResetPassword = ref(false);
    const emailValidationTimeout = ref<number | null>(null);
    const isEmailReadonly = ref(false);

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (props.fillEmail) {
        authenticateForm.value.email = props.fillEmail;
        await handleContinue();
      }
    });

    const handleContinue = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.checking_email, 'Verificăm email-ul...');
      try {
        isEmailExists.value = await authStore.checkEmailExists(<string>authenticateForm.value.email, await recaptcha(recaptchaInstance));
        isEmailChecked.value = true;
      } catch (error) {
        $toast.error(translation(translationList.value.error, 'A apărut o eroare'));
        console.error('Eroare la identificarea email-ului;');
      }
      showLoadingDialog.value = false;
    };

    watch(() => authenticateForm.value.email, (newValue, oldValue) => {
      if (!isEmailChecked.value) {
        return;
      }

      // Clear the previous timer if it exists
      if (emailValidationTimeout.value) {
        clearTimeout(emailValidationTimeout.value);
      }

      // Set a new timer to delay the validation
      emailValidationTimeout.value = window.setTimeout(async () => {
        if (newValue !== oldValue && newValue && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
          await handleContinue();
        }
      }, 1000); // Delay of 1 second
    });

    const handleLogin = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.authenticating, 'Autentificare...');
      if (!isLoginFormValid()) {
        showLoadingDialog.value = false;
        return;
      }
      try {
        await authStore.login({
          email: <string>authenticateForm.value.email,
          password: <string>authenticateForm.value.password,
          token: await recaptcha(recaptchaInstance)
        });
        if (props.fillEmail) {
          emit('close-dialog');
        } else {
          return navigateTo('/');
        }
      } catch (error) {
        $toast.error(translation(translationList.value.error_credentials, 'Adresa de email sau parola sunt greșite.'));
      }
      showLoadingDialog.value = false;
    };

    const isLoginFormValid = () => {
      let allValid = true;
      const loginFields = ['email', 'password', 'terms_agreement'];
      loginFields.forEach(field => {
        if (!validationAuthenticateStatus.value[field]) {
          console.log('Validation failed for field: ', field);
          window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
          allValid = false;
        }
      });
      return allValid;
    };

    const isRegisterFormValid = () => {
      let allValid = true;
      const registerFields = ['email', 'terms_agreement', 'phone_number', 'first_name', 'last_name'];

      if (!authenticateForm.value.is_default_password) {
        registerFields.push('password', 'password_confirmation');
      }

      registerFields.forEach(field => {
        if (!validationAuthenticateStatus.value[field]) {
          console.log('Validation failed for field: ', field);
          window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
          allValid = false;
        }
      });

      return allValid;
    };

    const handleRegister = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.registering, 'Înregistrare...');
      try {
        if (!isRegisterFormValid()) {
          showLoadingDialog.value = false;
          return;
        }
        const token = await recaptcha(recaptchaInstance);
        await authStore.register(
            <string>authenticateForm.value.email,
            <string>authenticateForm.value.phone_number,
            <string>authenticateForm.value.last_name,
            <string>authenticateForm.value.first_name,
            <string>authenticateForm.value.password,
            <string>authenticateForm.value.password_confirmation,
            <boolean>authenticateForm.value.is_default_password,
            <string>token
        );
        $toast.success(translation(translationList.value.account_created, 'Contul a fost creat cu succes!'));
        if (props.fillEmail) {
          emit('close-dialog');
        } else {
          return navigateTo('/');
        }
      } catch (error) {
        $toast.error(translation(translationList.value.error, 'A apărut o eroare'));
      }
      showLoadingDialog.value = false;
    };

    const handleGenerateResetPassword = async () => {
      isResetPassword.value = true;
      showLoadingDialog.value = true;
      isEmailReadonly.value = true;
      messageLoadingDialog.value = translation(translationList.value.sending_reset_email, 'Trimitem email-ul de resetare...');
      try {
        if (!validationAuthenticateStatus.value.email) {
          window.dispatchEvent(new CustomEvent('validate-customer', {detail: 'email'}));
          showLoadingDialog.value = false;
          $toast.error(translation(translationList.value.error_email, 'Adresa de email este greșită.'));
          return;
        }
        const token = await recaptcha(recaptchaInstance);
        await authStore.generateResetToken(<string>authenticateForm.value.email, <string>token);
        $toast.info(translation(translationList.value.reset_email_sent, 'Email-ul pentru restarea parolei a fost trimis!'));
      } catch (error) {
        $toast.error(translation(translationList.value.reset_email_not_sent, 'Email-ul pentru resetarea parolei nu a fost trimis!'));
      }
      showLoadingDialog.value = false;
    };

    const handleResetPassword = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.resetting_password, 'Resetează parola...');
      try {
        if (!isResetPasswordFormValid()) {
          showLoadingDialog.value = false;
          $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
          return;
        }
        const token = <string>await recaptcha(recaptchaInstance);
        await authStore.resetPassword(
            <string>authenticateForm.value.email,
            <string>authenticateForm.value.password,
            <string>authenticateForm.value.password_confirmation,
            <string>authenticateForm.value.reset_token,
            token
        );
        $toast.success(translation(translationList.value.password_reset_success, 'Parola contului a fost resetată cu succes!'));
        if (props.fillEmail) {
          emit('close-dialog');
        } else {
          return navigateTo('/');
        }
      } catch (error) {
        $toast.error(translation(translationList.value.password_reset_error, 'Parola contului nu a fost resetată!'));
      }
      showLoadingDialog.value = false;
    };

    const isResetPasswordFormValid = () => {
      let allValid = true;
      const resetPasswordFields = ['email', 'password', 'reset_token'];
      resetPasswordFields.forEach(field => {
        if (!validationAuthenticateStatus.value[field]) {
          console.log('Validation failed for field: ', field);
          window.dispatchEvent(new CustomEvent('validate-customer', {detail: field}));
          allValid = false;
        }
      });
      return allValid;
    };

    const handleValidationAuthenticateStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationAuthenticateStatus.value[field] = isValid;
    };

    return {
      authenticateForm,
      isEmailChecked,
      isEmailExists,
      isResetPassword,
      handleContinue,
      handleLogin,
      handleRegister,
      handleGenerateResetPassword,
      handleResetPassword,
      showLoadingDialog,
      messageLoadingDialog,
      translationList,
      translation,
      defaultVersion: props.defaultVersion,
      handleValidationAuthenticateStatus,
      isEmailReadonly
    };
  },
});
</script>

<template>
  <v-container class="background-surface" style="max-width: 1200px;">
    <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
    <!-- Auth messages -->
    <div>
      <v-row v-if="defaultVersion && !isEmailChecked && !isEmailExists && !isResetPassword"  class="my-3">
        <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{
              translation(translationList.account_check, 'Autentificare rapidă cu detectarea automată a contului')
            }}</h3>
          <p class="font-weight-regular">
            {{
              translation(translationList.account_check_helper, 'Introdu adresa de email pentru a fi direcționat automat către pagina de autentificare sau înregistrare, în funcție de existența contului.')
            }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="defaultVersion && isEmailChecked && isEmailExists && !isResetPassword" class="my-3">
        <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{ translation(translationList.account_login, 'Conectează-te la contul tău') }}</h3>
          <p class="font-weight-regular">
            {{
              translation(translationList.account_login_helper, 'Introdu parola și conectează-te rapid. Dacă ai uitat parola, reseteaz-o cu un singur clic.')
            }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="defaultVersion && isEmailChecked && !isEmailExists" class="my-3">
        <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{ translation(translationList.account_register, 'Creează-ți un cont nou') }}</h3>
          <p class="font-weight-regular">
            {{
              translation(translationList.account_register_helper, 'Completează-ți numele, prenumele și numărul de telefon pentru a-ți configura rapid contul.')
            }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="defaultVersion && isResetPassword" class="my-3">
        <v-col cols="12" sm="auto" md="6" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{ translation(translationList.account_reset, 'Reseteaza parola') }}</h3>
          <p class="font-weight-regular">
            {{
              translation(translationList.account_reset_helper, 'Te rugăm să te introduci codul primit pe adresa de email pentru a iti putea schimba parola.')
            }}
          </p>
        </v-col>
      </v-row>
    </div>

    <!-- Auth forms -->
    <div>
      <v-row>
        <!-- Default -->
        <v-col cols="12" sm="12" :md="isEmailChecked ? '6' : '12'">
          <customer-email
              v-model="authenticateForm.email"
              @validation-customer-status="handleValidationAuthenticateStatus"
              :is-readonly="isEmailReadonly"
          />
        </v-col>
        <!-- Login form -->
        <v-col v-if="isEmailChecked && isEmailExists && !isResetPassword" cols="12" sm="12" md="6">
          <customer-password
              v-model="authenticateForm.password"
              @validation-customer-status="handleValidationAuthenticateStatus"
          />
        </v-col>
        <!-- Register form -->
        <v-col v-if="isEmailChecked && !isEmailExists && !isResetPassword" cols="12" sm="12" md="6">
          <customer-phone-number
              v-model="authenticateForm.phone_number"
              @validation-customer-status="handleValidationAuthenticateStatus"
          >
          </customer-phone-number>
        </v-col>
        <!-- Reset password form -->
        <v-col v-if="isResetPassword" cols="12" sm="12" md="6">
          <customer-reset-token
              v-model="authenticateForm.reset_token"
              @validation-customer-status="handleValidationAuthenticateStatus"
          >
          </customer-reset-token>
        </v-col>
      </v-row>

      <!-- Check step -->
      <div v-if="!isEmailChecked">
        <v-row class="mt-10 mb-15">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
                class="block-on-mobile"
                color="primary"
                size="x-large"
                flat
                rounded="0"
                @click="handleContinue">
              {{ translation(translationList.continue, 'Continuă') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Login form -->
      <div v-if="isEmailChecked && isEmailExists && !isResetPassword">
        <v-row>
          <v-col cols="12">
            <customer-terms-agreement
                v-model="authenticateForm.terms_agreement"
                @validation-customer-status="handleValidationAuthenticateStatus">
            </customer-terms-agreement>
          </v-col>
        </v-row>
        <v-row class="mt-10">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
                class="block-on-mobile"
                size="x-large"
                variant="plain"
                rounded="0"
                @click="handleGenerateResetPassword"
            >
              {{ translation(translationList.reset_password, 'Ai uitat parola? Resetează acum') }}
            </v-btn>
          </v-col>
        </v-row>
        <v-row class="mb-15">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
                class="block-on-mobile"
                color="primary"
                size="x-large"
                flat
                rounded="0"
                @click="handleLogin"
            >
              {{ translation(translationList.login, 'Autentificare') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Register form -->
      <div v-if="isEmailChecked && !isEmailExists">
        <v-row class="mt-3">
          <v-col cols="12" sm="12" md="6">
            <customer-last-name
                v-model="authenticateForm.last_name"
                @validation-customer-status="handleValidationAuthenticateStatus"
            >
            </customer-last-name>
          </v-col>
          <v-col cols="12" sm="12" md="6">
            <customer-first-name
                v-model="authenticateForm.first_name"
                @validation-customer-status="handleValidationAuthenticateStatus"
            >
            </customer-first-name>
          </v-col>
        </v-row>
        <v-row class="my-3">
          <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
            <h3 class="font-weight-bold">
              {{ translation(translationList.password_default, 'Configurare automată a parolei') }}</h3>
            <p class="font-weight-regular">
              {{
                translation(translationList.password_default_helper, 'Contul tău va fi creat cu o parolă generată automat și trimisă pe email. Debifează dacă preferi să alegi tu parola.')
              }}
            </p>
          </v-col>
          <v-col cols="12" sm="12" md="6" class="d-flex justify-end left-on-mobile">
            <v-checkbox
                class="pt-6"
                v-model="authenticateForm.is_default_password"
                :label="translation(translationList.default_password, 'Creează cont cu parolă generată automat')"
            />
          </v-col>
          <v-col cols="12" sm="12" md="6" v-if="!authenticateForm.is_default_password">
            <customer-password
                v-model="authenticateForm.password"
                @validation-customer-status="handleValidationAuthenticateStatus"
            />
          </v-col>
          <v-col cols="12" sm="12" md="6" v-if="!authenticateForm.is_default_password">
            <customer-password
                v-model="authenticateForm.password_confirmation"
                @validation-customer-status="handleValidationAuthenticateStatus"
                :is-password-confirmation="true"
            />
          </v-col>
        </v-row>
        <v-row class="mt-3">
          <v-col cols="12">
            <customer-terms-agreement
                v-model="authenticateForm.terms_agreement"
                @validation-customer-status="handleValidationAuthenticateStatus">
            </customer-terms-agreement>
          </v-col>
        </v-row>
        <v-row class="mt-10 mb-15">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
                class="block-on-mobile"
                color="primary"
                size="x-large"
                flat
                rounded="0"
                @click="handleRegister"
            >
              {{ translation(translationList.register, 'Înregistrare') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Reset password form -->
      <div v-if="isResetPassword">
        <v-row>
          <v-col cols="12" sm="12" md="6">
            <customer-password
                v-model="authenticateForm.password"
                @validation-customer-status="handleValidationAuthenticateStatus"
            />
          </v-col>
          <v-col cols="12" sm="12" md="6">
            <customer-password
                v-model="authenticateForm.password_confirmation"
                @validation-customer-status="handleValidationAuthenticateStatus"
                :is-password-confirmation="true"
            />
          </v-col>
        </v-row>
        <v-row class="mt-10 mb-15">
          <v-col cols="12" class="d-flex align-center justify-center">
            <v-btn
                class="block-on-mobile"
                color="primary"
                size="x-large"
                flat
                rounded="0"
                @click="handleResetPassword"
            >
              {{ translation(translationList.reset_password, 'Resetează parola') }}
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </div>

  </v-container>
</template>

<style scoped>
</style>